"use client";

import * as Sentry from "@sentry/nextjs";
import { gql } from "graphql-request";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { analytics, clearSignupMarker, hasSignupMarker } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import { graphqlRequest } from "@/lib/graphql/client";
import { clearOrgIdTag, setOrgIdTag } from "@/lib/sentry/issue-tags";

const EXCLUDED_PATH_PATTERN = /^\/admin(\/|$)/;

const ACK_SIGNUP_CONVERSION = gql`
  mutation AckSignupConversion {
    ackSignupConversion
  }
`;

/** true = this tab won the one-shot; false = already claimed; null = request failed. */
const claimSignupConversion = async (): Promise<boolean | null> => {
  try {
    const data = await graphqlRequest<{ ackSignupConversion: boolean }>(ACK_SIGNUP_CONVERSION);
    return data.ackSignupConversion === true;
  } catch {
    return null;
  }
};

const CLAIM_RETRY_ATTEMPTS = 4;
const CLAIM_RETRY_DELAY_MS = 2000;

const beginSignupClaim = (
  inFlight: { current: boolean },
  timeoutRef: { current: number | null },
  cancelledRef: { current: boolean },
  onSettled?: (claimed: boolean) => void,
): void => {
  if (inFlight.current) {
    return;
  }
  inFlight.current = true;

  const attempt = (remaining: number): void => {
    void claimSignupConversion().then((claimed) => {
      // A settled ack is already consumed server-side. Still emit/clear even
      // if this instance unmounted (Strict Mode remount, Suspense, navigation).
      // Only skip further retries after cancel.
      if (claimed !== null) {
        onSettled?.(claimed);
        return;
      }
      if (cancelledRef.current || remaining <= 0) {
        inFlight.current = false;
        return;
      }
      timeoutRef.current = window.setTimeout(() => {
        if (cancelledRef.current) {
          inFlight.current = false;
          return;
        }
        attempt(remaining - 1);
      }, CLAIM_RETRY_DELAY_MS);
    });
  };

  attempt(CLAIM_RETRY_ATTEMPTS - 1);
};

/**
 * Syncs session identity with analytics destinations, then records page views.
 * Must live under AuthProvider so identify/reset always run before pageView
 * (HubSpot only sends identify on the next trackPageView).
 *
 * Also consumes a one-shot signup marker (set on /signup before AuthKit) and
 * emits `sign_up` (and the object-style GTM `signup_complete` data-layer event)
 * only after this tab wins ackSignupConversion (server one-shot) for
 * non-admin users.
 */
export const AnalyticsTracker = () => {
  const { user, isMePending, isAdmin, signupConversionPending } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const identityKey = user?.id ?? user?.email ?? "";
  const previousIdentityKeyRef = useRef<string | null>(null);
  const signupClaimInFlightRef = useRef(false);
  const signupClaimTimeoutRef = useRef<number | null>(null);
  const signupClaimCancelledRef = useRef(false);

  useEffect(() => {
    signupClaimCancelledRef.current = false;
    return () => {
      signupClaimCancelledRef.current = true;
      if (signupClaimTimeoutRef.current !== null) {
        window.clearTimeout(signupClaimTimeoutRef.current);
        signupClaimTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isMePending) {
      return;
    }

    const previousIdentityKey = previousIdentityKeyRef.current;

    if (identityKey) {
      analytics.identify({ email: user.email, id: user.id });
      Sentry.setUser({ id: user.id });
      setOrgIdTag(user.org_id);

      if (isAdmin === true) {
        clearSignupMarker();
        // Superusers never emit sign_up, but still clear conversion_pending so it
        // cannot linger forever after a platform account signs up via /signup.
        if (signupConversionPending === true) {
          beginSignupClaim(signupClaimInFlightRef, signupClaimTimeoutRef, signupClaimCancelledRef);
        }
      } else if (isAdmin === false && signupConversionPending !== null) {
        if (signupConversionPending && hasSignupMarker()) {
          beginSignupClaim(signupClaimInFlightRef, signupClaimTimeoutRef, signupClaimCancelledRef, (claimed) => {
            clearSignupMarker();
            if (claimed) {
              analytics.track("sign_up");
            }
          });
        } else if (signupConversionPending && !hasSignupMarker()) {
          // Authenticated without a marker (e.g. /login cleared it) — disarm pending
          // so a later /signup revisit cannot false-fire sign_up.
          beginSignupClaim(signupClaimInFlightRef, signupClaimTimeoutRef, signupClaimCancelledRef);
        } else if (!signupConversionPending) {
          clearSignupMarker();
        }
      }
    } else if (previousIdentityKey) {
      analytics.reset();
      Sentry.setUser(null);
      clearOrgIdTag();
    }

    previousIdentityKeyRef.current = identityKey;

    if (!pathname || EXCLUDED_PATH_PATTERN.test(pathname)) {
      return;
    }

    const qs = searchParams?.toString();
    analytics.pageView(qs ? `${pathname}?${qs}` : pathname);
  }, [
    identityKey,
    isAdmin,
    isMePending,
    pathname,
    searchParams,
    signupConversionPending,
    user?.email,
    user?.id,
    user?.org_id,
  ]);

  return null;
};
